// The Tax Savings Packet's page organizer (Philip 2026-10-30): one thumbnail per page; drag a page to move it, ← → to
// move it one place without dragging, × to delete it. The caller owns the order and its undo history.
//
// ⛔ POINTER EVENTS, NOT HTML5 DRAG-AND-DROP. The first version used draggable / onDrop and reordering did nothing on
// wigleyhub.com: the <img> inside each tile starts its own native image drag, and Safari will not begin a drag without
// dataTransfer data. Pointer events behave the same for a mouse and a trackpad in Chrome and Safari. A press that moves
// less than DRAG_START pixels is not a drag, so the buttons on a tile still click.
import React, { useRef, useState } from 'react';

const DRAG_START = 5;
export const moveItem = (list, from, to) => {
  if (from === to || to < 0 || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

// order: page indices in packet order · thumbs[i] / labels[i]: page i's image and caption · onChange(nextOrder)
export default function PageOrganizer({ order, thumbs, labels, onChange, colors }) {
  const c = colors;
  const [drag, setDrag] = useState(null);   // { from, over, x, y } while dragging
  const press = useRef(null);               // { from, x, y, id } between pointerdown and the drag starting

  const tileAt = (x, y) => {
    const el = document.elementFromPoint(x, y)?.closest('[data-page-pos]');
    return el ? Number(el.getAttribute('data-page-pos')) : null;
  };
  const onPointerDown = (e, pos) => {
    if (e.button !== 0 || e.target.closest('button')) return;
    press.current = { from: pos, x: e.clientX, y: e.clientY, id: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const p = press.current;
    if (!p || e.pointerId !== p.id) return;
    if (!drag && Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_START) return;
    const over = tileAt(e.clientX, e.clientY);
    setDrag({ from: p.from, over: over ?? drag?.over ?? p.from, x: e.clientX, y: e.clientY });
  };
  const finish = (e) => {
    const p = press.current;
    if (!p || e.pointerId !== p.id) return;
    press.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (drag && drag.over != null && drag.over !== drag.from) onChange(moveItem(order, drag.from, drag.over));
    setDrag(null);
  };

  const small = { width: '22px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer', fontSize: '12px', lineHeight: '22px', padding: 0 };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', userSelect: 'none' }}>
      {order.map((pageIx, pos) => {
        const lifted = drag && drag.from === pos;
        const target = drag && drag.over === pos && drag.from !== pos;
        return (
          <div key={`${pageIx}-${pos}`} data-page-pos={pos}
            onPointerDown={(e) => onPointerDown(e, pos)} onPointerMove={onPointerMove} onPointerUp={finish} onPointerCancel={finish}
            style={{ width: '150px', position: 'relative', cursor: drag ? 'grabbing' : 'grab', touchAction: 'none', opacity: lifted ? 0.35 : 1,
              outline: target ? '2px solid var(--red)' : 'none', outlineOffset: '3px', borderRadius: '4px' }}>
            <img alt={`Page ${pos + 1}`} src={thumbs[pageIx]} draggable={false} onDragStart={(e) => e.preventDefault()}
              style={{ width: '150px', border: `1px solid ${c.border}`, borderRadius: '4px', background: '#fff', display: 'block', pointerEvents: 'none' }} />
            <button type="button" title="Delete this page" onClick={() => onChange(order.filter((_, i) => i !== pos))}
              style={{ ...small, position: 'absolute', top: '4px', right: '4px', background: 'rgba(180,0,0,0.85)', color: '#fff', fontSize: '13px' }}>×</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
              <button type="button" title="Move one place earlier" disabled={pos === 0} onClick={() => onChange(moveItem(order, pos, pos - 1))}
                style={{ ...small, background: c.soft, color: c.text, opacity: pos === 0 ? 0.35 : 1, cursor: pos === 0 ? 'default' : 'pointer' }}>←</button>
              <span style={{ fontSize: '11px', fontWeight: 700, flex: 1, textAlign: 'center' }}>{pos + 1}</span>
              <button type="button" title="Move one place later" disabled={pos === order.length - 1} onClick={() => onChange(moveItem(order, pos, pos + 1))}
                style={{ ...small, background: c.soft, color: c.text, opacity: pos === order.length - 1 ? 0.35 : 1, cursor: pos === order.length - 1 ? 'default' : 'pointer' }}>→</button>
            </div>
            <div style={{ fontSize: '10px', color: c.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={labels[pageIx]}>{labels[pageIx]}</div>
          </div>
        );
      })}
      {drag && (
        <img alt="" src={thumbs[order[drag.from]]} style={{ position: 'fixed', left: drag.x - 40, top: drag.y - 50, width: '80px', opacity: 0.85,
          pointerEvents: 'none', zIndex: 2000, boxShadow: '0 6px 18px rgba(0,0,0,0.3)', borderRadius: '3px' }} />
      )}
    </div>
  );
}
